/**
 * Minimal exact decimal arithmetic for order rules (prices, sizes, notionals).
 * A value is `n / 10^s` with `n` a bigint, so there is no float drift: 1 - 0.58504 is exactly 0.41496,
 * and 0.1 * 3 is exactly 0.3.
 */

export interface Dec {
  /** Unscaled integer value. */
  n: bigint;
  /** Number of decimal places (>= 0). */
  s: number;
}

export type RoundMode = 'nearest' | 'down' | 'up';

const DEC_RE = /^([+-])?(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

const pow10 = (k: number): bigint => 10n ** BigInt(k);

/** Strip trailing zeros from the fractional part. */
export function normalize(d: Dec): Dec {
  let { n, s } = d;
  while (s > 0 && n % 10n === 0n) {
    n /= 10n;
    s -= 1;
  }
  return { n, s };
}

/**
 * Parse a decimal string ("0.58504", "95.0", ".5", "1e-5") or a finite number. Numbers are read through their
 * shortest round-trip string (String(0.1) === "0.1"), so 0.1 parses as exactly 1/10. Returns null if invalid.
 */
export function parseDec(v: number | string): Dec | null {
  let str: string;
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return null;
    str = String(v);
  } else {
    str = v.trim();
  }
  const m = DEC_RE.exec(str);
  if (!m) return null;
  const [, sign, intPart = '', fracPart = '', expPart] = m;
  if (intPart === '' && fracPart === '') return null;
  let n = BigInt((intPart || '0') + fracPart);
  let s = fracPart.length;
  const exp = expPart === undefined ? 0 : Number(expPart);
  if (!Number.isSafeInteger(exp) || Math.abs(exp) > 400) return null;
  if (exp > 0) {
    if (exp <= s) s -= exp;
    else {
      n *= pow10(exp - s);
      s = 0;
    }
  } else if (exp < 0) {
    s += -exp;
  }
  if (sign === '-') n = -n;
  return normalize({ n, s });
}

/** Plain decimal string, trailing zeros stripped (same shape as the SDK's float_to_wire). */
export function decToString(d: Dec): string {
  const { n, s } = normalize(d);
  const neg = n < 0n;
  const digits = (neg ? -n : n).toString();
  if (s === 0) return (neg ? '-' : '') + digits;
  const padded = digits.padStart(s + 1, '0');
  const out = `${padded.slice(0, -s)}.${padded.slice(-s)}`;
  return (neg ? '-' : '') + out;
}

export function decToNumber(d: Dec): number {
  return Number(decToString(d));
}

function align(a: Dec, b: Dec): [bigint, bigint, number] {
  const s = Math.max(a.s, b.s);
  return [a.n * pow10(s - a.s), b.n * pow10(s - b.s), s];
}

export function cmp(a: Dec, b: Dec): -1 | 0 | 1 {
  const [x, y] = align(a, b);
  return x < y ? -1 : x > y ? 1 : 0;
}

export function add(a: Dec, b: Dec): Dec {
  const [x, y, s] = align(a, b);
  return normalize({ n: x + y, s });
}

export function sub(a: Dec, b: Dec): Dec {
  const [x, y, s] = align(a, b);
  return normalize({ n: x - y, s });
}

export function mul(a: Dec, b: Dec): Dec {
  return normalize({ n: a.n * b.n, s: a.s + b.s });
}

export const ZERO: Dec = { n: 0n, s: 0 };
export const ONE: Dec = { n: 1n, s: 0 };

export function fromInt(i: number | bigint): Dec {
  return { n: BigInt(i), s: 0 };
}

/** Number of decimal places once trailing zeros are stripped. */
export function decimalsOf(d: Dec): number {
  return normalize(d).s;
}

/** Significant figures (trailing zeros of the fraction stripped; for integers, trailing zeros are not counted). */
export function sigFigsOf(d: Dec): number {
  const { n } = normalize(d);
  if (n === 0n) return 0;
  let digits = (n < 0n ? -n : n).toString();
  digits = digits.replace(/0+$/, '');
  return digits.length;
}

/**
 * Round to `decimals` places. 'nearest' is half away from zero, 'down' toward zero, 'up' away from zero.
 * (Prices and sizes are positive, so 'down'/'up' are floor/ceil for them.)
 */
export function roundDec(d: Dec, decimals: number, mode: RoundMode = 'nearest'): Dec {
  if (!Number.isInteger(decimals) || decimals < 0) throw new Error(`invalid decimals: ${decimals}`);
  const nd = normalize(d);
  if (nd.s <= decimals) return nd;
  const div = pow10(nd.s - decimals);
  const neg = nd.n < 0n;
  const abs = neg ? -nd.n : nd.n;
  let q = abs / div;
  const r = abs % div;
  if (r !== 0n) {
    if (mode === 'up' || (mode === 'nearest' && r * 2n >= div)) q += 1n;
  }
  return normalize({ n: neg ? -q : q, s: decimals });
}
